import _ from 'lodash';
import * as members from '../privates/privateMembers';
import reporter from '../privates/reporter';
import * as postMessage from '../privates/postMessage';
import platformUtils from '../privates/platformUtils';
import * as urlUtils from '../privates/urlUtils';
import {validate} from '../privates/validations/validation';
import {capitalize} from '../privates/utils';
import Multilingual from '../privates/multilingual';
import {setStageUnfriendlyFunctions} from '../privates/utils/livePreview';

const WINDOW_COMP_ID = 'WINDOW';

/**
 * @namespace wix-window
 * @summary The wix-window module contains functionality that pertains to the
 *  current browser window.
 * @description
 *  To use the window module, import `wixWindow` from the `wix-window` module:
 *
 *    ```javascript
 *    import wixWindow from 'wix-window';
 *    ```
 *
 *  The APIs in `wix-window` can only be used in front-end code.
 */

class Lightbox {
  constructor(appDefId){
    members._appDefId.set(this, appDefId);
  }

  /**
   * @function getContext
   * @summary Returns the data object that was passed to a lightbox.
   * @description
   *  The `getContext()` function returns the data object passed when the lightbox
   *  was opened using the [`openLightbox()`](wix-window.html#openLightbox) function,
   *  if any data was passed. If the lightbox was opened another way,
   *  `getContext()` returns `undefined`.
   *
   *  To pass data to the lightbox that is opened, you must open the lightbox
   *  programmatically using the [`openLightbox()`](wix-window.html#openLightbox)
   *  function. If the lightbox is opened automatically when the page loads or
   *  by a link from a page element, data will not be passed to the lightbox.
   *  Therefore, if you want to pass data to the lightbox, make sure
   *  **Automatically display lightbox on pages** is <a href="https://support.wix.com/en/article/setting-which-page-your-lightbox-appears-on" target="_blank">set</a> to **No** in the
   *  **Lightbox Settings** panel in the Editor and don't set any element's link
   *  to open the lightbox. Instead, create your own method for opening
   *  the lightbox programmatically. For example, you can add a button with an `onClick` event
   *  handler that calls the [`openLightbox()`](wix-window.html#openLightbox) function.
   * @snippet [Lightbox-getContext.es6=Get the data that was passed to the lightbox]
   * @snippet [Lightbox-scenario.es6=A scenario where information is passed between a page and a lightbox]
   *  This example demonstrates how to pass data from a page to a lightbox that
   *  it opens and from the lightbox back to the page as it closes.
   *
   *  It assumes that the page has:
   *
   *  + An open button that is used to open the lightbox.
   *  + Two text inputs where information that is to be passed to the lightbox is entered.
   *  + Two text elements where information that is passed from the lightbox is displayed.
   *
   *
   *  It assumes that the lightbox has:
   *
   *  + An close button that is used to close the lightbox.
   *  + Two text inputs where information that is to be passed to the page is entered.
   *  + Two text elements where information that is passed from the page is displayed.
   * @returns {Object} The data object that was passed to the lightbox.
   * @memberof wix-window.lightbox
   */
  getContext() {
    return platformUtils.getRGI().getLightboxContext();
  }

  /**
   * @function close
   * @summary Closes the lightbox.
   * @description
   *  The `close()` function closes the lightbox and returns the given data to the
   *  [`openLightbox()`](wix-window.html#openLightbox) function that was used to
   *  open the lightbox.
   *
   *  To pass data back to the page that opened the lightbox, you must close the
   *  lightbox programmatically using the `close()` function. If the lightbox is
   *  closed by the site visitor clicking the 'X' icon, close button, or lightbox
   *  overlay, data will not be passed back the the page that opened the lightbox.
   *  Therefore, if you want to make sure data is passed back to the page that
   *  opened the lightbox, <a href="https://support.wix.com/en/article/setting-how-your-lightbox-is-closed" target="_blank">disable
   *  all of the methods mentioned above</a> and create your own method for closing
   *  the lightbox programmatically. For example, you can add a button with an `onClick` event
   *  handler that calls the `close()` function.
   * @snippet [Lightbox-close.es6=Close the lightbox]
   * @snippet [Lightbox-close_options.es6=Close the lightbox and pass back data to the opening function]
   * @snippet [Lightbox-scenario.es6=A scenario where information is passed between a page and a lightbox]
   *  This example demonstrates how to pass data from a page to a lightbox that
   *  it opens and from the lightbox back to the page as it closes.
   *
   *  It assumes that the page has:
   *
   *  + An open button that is used to open the lightbox.
   *  + Two text inputs where information that is to be passed to the lightbox is entered.
   *  + Two text elements where information that is passed from the lightbox is displayed.
   *
   *
   *  It assumes that the lightbox has:
   *
   *  + An close button that is used to close the lightbox.
   *  + Two text inputs where information that is to be passed to the page is entered.
   *  + Two text elements where information that is passed from the page is displayed.
   * @param {Object} [data] The data to pass back to [`openLightbox()`](wix-window.html#openLightbox) function.
   * @memberof wix-window.lightbox
   */
  close(returnedData){
    const contextId = platformUtils.getRGI().getContextId();
    const popups = platformUtils.getRMI('wix-window.lightbox.close').getSiteStructure().lightboxes;
    if (!_.find(popups, {id: contextId})) {
      reporter.reportSdkError(`The current page is not a lightbox and therefore cannot be closed`);
      return;
    }
    postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
        command: postMessage.MessageTypes.CLOSE_LIGHTBOX,
        data: {
            id: contextId,
            returnedData
        },
        appDefinitionId: members._appDefId.get(this)
    });
  }
}

class RenderingContext {
    /**
     * @member renderCycle
     * @label property
     * @summary **Deprecated:** Gets the current cycle in the rendering process.
     * @description
     *  **Deprecation note:** The `renderCycle` property no longer works as expected.
     *  It will always return `1`.
     * @type {Number}
     * @memberof wix-window.rendering
     * @readonly
     **/
    get renderCycle() {
        return Number(platformUtils.getQueryParameter("renderCycle")) || NaN;
    }

    /**
     * @member env
     * @label property
     * @summary Gets the current environment the rendering process is running in.
     * @description
     *  When possible, the rendering process is split in two in order to
     *  improve performance. The first cycle in the process happens server-side
     *  and the second cycle happens client-side. When not possible, all rendering
     *  happens client-side.
     *
     *  The `env` property returns `"backend"` when rendering on the server
     *  and `"browser"` when rendering on the client.
     *
     *  Use the `env` property in your page's [`onReady()`]($w.html#onReady)
     *  event handler to control where your code runs during the rendering
     *  process and to prevent code that causes side effects from running twice.
     * @note
     *  Rendering never occurs server-side when previewing your site.
     * @snippet [$w-onReady-env.es6=Use the Rendering API to avoid inserting an item twice]
     *  This example demonstrates how to make sure a collection insertion in the `onReady` 
     *  event handler occurs only once.
     * @type {String}
     * @memberof wix-window.rendering
     * @readonly
     **/
    get env() {
        return platformUtils.getEnv();
    }

    /**
     * @member warmupData
     * @label property
     * @summary **Deprecated:** Gets data sent from server-side code to client-side code.
     * @description
     *  **Deprecation note:** The `warmupData` property can no longer be used in a reliable
     *  manner.
     * 
     *  When possible, the rendering process is split in two in order to
     *  improve performance. The first cycle in the process happens server-side
     *  and the second cycle happens client-side. When not possible, all rendering
     *  happens client-side.
     *
     *  Pass data from the server-side cycle to the client-side cycle by
     *  returning the data from the [`onReady()`]() function when it is run server-side.
     *  Then retrieve that data client-side by getting the value of the
     *  `warmupData` property.
     * @note
     *  Rendering never occurs server-side when previewing your site.
     * @type {Boolean|Number|String|Object}
     * @memberof wix-window.rendering
     * @readonly
     **/
    get warmupData() {
        const uw = platformUtils.getQueryParameter("userWarmup");
        return uw && uw['wix-code'];
    }
}

class Window {
    constructor(appDefId){
        members._appDefId.set(this, appDefId);
        this.portData = {
            port: null,
            callbacks: [],
            requestInPorcess: false
        };
        this._multilingual = new Multilingual(appDefId);
        _.bindAll(this, ['scrollTo', 'getBoundingRect', 'getCurrentGeolocation', 'openLightbox', 'openModal', 'postMessage', 'getContainerPort']);
    }

    /**
     * @namespace wix-window.rendering
     * @summary The Rendering API is used to control when code is run as a page is being loaded.
     * @description
     *  When possible, the rendering process that runs when your page loads is
     *  split in two in order to improve performance. The first cycle in the process
     *  happens server-side and the second cycle happens client-side. When not
     *  possible, all rendering happens client-side.
     *
     *  Use the Rendering API to track when and where your code is running to
     *  selectively run parts of your code in certain conditions.
     *
     *  To use the Rendering API, import `wixWindow` from the `wix-window` module:
     *
     *    ```javascript
     *    import wixWindow from 'wix-window';
     *    ```
     */
    get rendering() {
        return new RenderingContext();
    }

    /**
     * @namespace wix-window.lightbox
     * @summary A <a href="https://support.wix.com/en/article/about-lightboxes" target="_blank">lightbox</a> pops up on your site to grab your visitor's attention.
     * @description
     *  To use the Lightbox API, import `wixWindow` from the `wix-window` module:
     *
     *    ```javascript
     *    import wixWindow from 'wix-window';
     *    ```
     *
     * The APIs in `wix-window.lightbox` can only be used in front-end code.
     */
    get lightbox() {
        const appDefinitionId = members._appDefId.get(this);
        return new Lightbox(appDefinitionId);
    }

    /**
     * @member browserLocale
     * @label property
     * @summary Gets the locale of the site visitor's browser.
     * @description
     *  A locale, also known as an IETF language tag, is an abbreviated code
     *  that defines the language, country, and other aspects of the site 
     *  visitor's browser, such as number format and date format.
     *
     *  Some common locales include:
     *
     *  + `"en-US"`: English, United States
     *  + `"en-GB"`: English, British
     *  + `"es-ES"`: Spanish, Spain
     *  + `"de-DE"`: German, Germany
     *  + `"ja-JP"`: Japanese, Japan
     *  + `"fr-CH"`: French, Switzerland
     *  + `"it-IT"`: Italian, Italy
     * @snippet [Window-browserLocale.es6=Get the locale of a visitor's browser]
     * @type {String}
     * @memberof wix-window
     * @readonly
     */
    get browserLocale() {
        return platformUtils.getRGI().getRegionalSettings().browserLocale;
    }

    /**
     * @member viewMode
     * @label property
     * @summary Gets which mode the site is currently being viewed in.
     * @description
     *  The `viewMode` property gets either:
     *
     *  + `"Preview"`: When previewing the site using the **Preview** button in the Editor.
     *  + `"Site"`: When viewing the published site.
     * @snippet [Window-viewMode.es6=Get a window's view mode]
     * @type {String}
     * @memberof wix-window
     * @readonly
     */
    get viewMode() {
        if (platformUtils.getRGI().getLivePreviewMode && platformUtils.getRGI().getLivePreviewMode()) {
            return "Editor";
        }
        return capitalize(platformUtils.getQueryParameter('viewMode'));
    }

    /**
     * @member formFactor
     * @label property
     * @summary Gets what kind of device is being used to view the page.
     * @description
     *  The `formFactor` property gets one of:
     *
     *  + `"Desktop"`: When viewed in a desktop browser.
     *  + `"Mobile"`: When viewed in a mobile browser.
     *  + `"Tablet"`: When viewed in a tablet browser.
     * @snippet [Window-formFactor.es6=Get a device's form factor]
     * @type {String}
     * @memberof wix-window
     * @readonly
     */
    get formFactor() {
        const formFactor = platformUtils.getRGI().getDeviceType() || 'desktop';
        return capitalize(formFactor);
    }

    /**
     * @member locale
     * @label property
     * @summary Gets the locale of the current environment.
     * @description
     *  Deprecation note: The `locale` property is being deprecated. Use the
     *  [`browserLocale`](#browserLocal) property instead.
     * 
     *  A locale, also known as an IETF language tag, is an abbreviated code
     *  that defines the user's language, country, and other aspects of the user
     *  interface such as number format and date format.
     *
     *  Some common locales include:
     *
     *  + `"en-US"`: English, United States
     *  + `"en-GB"`: English, British
     *  + `"es-ES"`: Spanish, Spain
     *  + `"de-DE"`: German, Germany
     *  + `"ja-JP"`: Japanese, Japan
     *  + `"fr-CH"`: French, Switzerland
     *  + `"it-IT"`: Italian, Italy
     * @snippet [Window-locale.es6=Get an environment's locale]
     * @type {String}
     * @memberof wix-window
     * @readonly
     */
    get locale() {
        return platformUtils.getQueryParameter("locale");
    }

    /**
     * @member referrer
     * @label property
     * @summary Gets the HTTP referrer header field.
     * @description
     *  The `referrer` is the address of the previous web page that the user was
     *  on before arriving at the current page, typically by clicking a link.
     * @note
     *  When visitors move from page to page within your site, the `referrer` property
     *  does not contain the address of the page the visitor came from. This is because
     *  Wix sites are built as <a href="https://en.wikipedia.org/wiki/Single-page_application" target="_blank">single page applications</a>.
     *  To get the previous page a visitor was visiting within your site, you can
     *  use [`wix-storage`](wix-storage.html) to store the visitor's current page
     *  and retrieve the visitor's previous page.
     * @snippet [Window-referrer.es6=Get the referrer information]
     * @snippet [Window-referrer_workaround.es6=Get the previous page within a Wix site]
     *  This example demonstrates how to use session storage to know which page
     *  is the page a site visitor previously visited. The code below needs to be
     *  placed in the **Site** tab so that it runs on all your site's pages. The
     *  code retrieves the URL of the page a visitor previously visited from session
     *  storage. It then stores the URL of the vistor's current page in session
     *  storage. When the visitor navigates to another page, this stored URL is
     *  retrieved as the previous page URL.
     * @type {String}
     * @memberof wix-window
     * @readonly
     **/
    get referrer() {
        return platformUtils.getQueryParameter("referrer");
    }

    /**
     * @typedef {Object} wix-window~WindowSizeInfo
     * @summary An object returned by the `getBoundingRect()` function that contains information about the window's size, the document's size, and the current scroll position.
     * @property {Object} window An object with `height` and `width` key:value pairs containing the size of the viewable area of the current browser window.
     * @property {Object} document An object with `height` and `width` key:value pairs containing the size of the actual body of the page, which may be larger or smaller than the current window.
     * @property {Object} scroll An object with `x` and `y` key:value pairs containing the scroll offset of the page within the window from the top-left corner.
     * @snippet [Window-getBoundingRect.es6=Get information about the window]
     * @see [getBoundingRect( )](#getBoundingRect)
     */

    /**
     * @function getBoundingRect
     * @summary Returns information about the window.
     * @description
     *  The `getBoundingRect()` function returns a Promise that resolves to an object
     *  containing information about the current window's size, the document's size,
     *  and the current scroll position.
     * @returns {Promise}
     * @fulfill {wix-window~WindowSizeInfo} An object containing information about the window's size, the document's size, and the current scroll position.
     * @snippet [Window-getBoundingRect.es6=Get information about the window]
     * @memberof wix-window
     */
     getBoundingRect() {
        return new Promise((resolve, reject) => {
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.WINDOW_BOUNDING_RECT,
                appDefinitionId: members._appDefId.get(this),
                callback
            });
        });
    }

    /**
     * @function copyToClipboard
     * @summary Copy text to clipboard. If the current browser does not support programmatic copy action, a modal with a copy button will be opened.
     * @description
     *  The `copyToClipboard()` function returns a Promise that resolves when the text is copied to clipboard.
     * @returns {Promise}
     * @fulfill {void} When the copy flow is complete.
     * @reject {Error} The error that caused the rejection.
     * @snippet [Window-copyToClipboard.es6=Copy text to clipboard]
     * @memberof wix-window
     */
    copyToClipboard(text) {
        return new Promise((resolve, reject) => {
            if(!validate({
                compType: 'Window',
                functionName: 'copyToClipboard',
                dataToValidate: {text}
            })) {
                reject({error: 'unable to copy null value'});
                return;
            }
            const callback = (data, error) => { return error ? reject(error) : resolve(); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.COPY_TO_CLIPBOARD,
                data: {text},
                callback
            });
        });
    }

    /**
     * @typedef {Object} wix-window~ScrollToOptions
     * @summary An object used for providing options for the `scrollTo()` method.
     * @property {Boolean} scrollAnimation Indicates whether to scroll with an animation. Defaults to `true`.
     * @snippet [Window-scrollTo_without_animation=Scroll the page to a location without an animation]
     * @see [scrollTo( )](#scrollTo)
     */

    /**
     * @function scrollTo
     * @summary Scrolls the page to a specific location.
     * @description
     *  The `scrollTo()` function returns a Promise that resolves when the current
     *  page has been scrolled to the given location.
     *
     *  The `x` and `y` parameters determine the top-left pixel that is
     *  displayed on screen after the scroll.
     *
     *  To scroll to a specific element on the page, see the `$w.Node`
     *  [`scrollTo()`]($w.Node.html) function.
     * 
     *  Use the `options` parameter to specify the options to use when scrolling.
     * @snippet [Window-scrollTo.es6=Scroll the page to a location]
     * @snippet [Window-scrollTo_then.es6=Scroll the page to a location and log message when done]
     * @snippet [Window-scrollTo_without_animation=Scroll the page to a location without an animation]
     * @param {Number} x The horizontal position, in pixels, to scroll to.
     * @param {Number} y The vertical position, in pixels, to scroll to.
     * @param {wix-window~ScrollToOptions} [options] Scrolling options.
     * @returns {Promise}
     * @fulfill {void} When the scroll is complete.
     * @memberof wix-window
     */
    scrollTo(x, y, options) {
        const validtionObj = {};
        if (!_.isNil(x)) {
            validtionObj.x = x;
        }
        if (!_.isNil(y)) {
            validtionObj.y = y;
        }
        if (!_.isNil(options)) {
            validtionObj.options = options;
        }
        return new Promise((resolve, reject) => {
            if (!validate({
                compType: 'Window',
                functionName: 'scrollTo',
                dataToValidate: validtionObj
            })) {
                reject({});
                return;
            }
            const args = {x, y, scrollAnimation: _.get(options, 'scrollAnimation', true), requiresDom: {compId: WINDOW_COMP_ID}};
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.SCROLL_TO,
                data: args,
                appDefinitionId: members._appDefId.get(this),
                callback
            });
        });
    }

    /**
     * @function scrollBy
     * @summary Scrolls the page by a given number of pixels.
     * @description
     *  The `scrollBy()` function returns a Promise that resolves when the current
     *  page has been scrolled by the given number of pixels,
     *
     *  The `x` and `y` parameters determine the number of horizontal and vertical
     *  pixels to scroll the current page. Negative numbers scroll up or to the
     *  left and positive numbers scroll down or to the right.
     * @snippet [Window-scrollBy.es6=Scroll the page by a given number of pixels]
     * @snippet [Window-scrollBy_then.es6=Scroll the page by a given number of pixels and log message when done]
     * @param {Number} x The horizontal offset, in pixels, to scroll by.
     * @param {Number} y The vertical offset, in pixels, to scroll by.
     * @returns {Promise}
     * @fulfill {void} When the scroll is complete.
     * @memberof wix-window
     */
    scrollBy(x, y) {
        if (_.isNil(x)) {
            x = 0;
        }
        if (_.isNil(y)) {
            y = 0;
        }
        return new Promise((resolve, reject) => {
            const args = {x, y, requiresDom: {compId: WINDOW_COMP_ID}};
            if (!validate({
                compType: 'Window',
                functionName: 'scrollBy',
                dataToValidate: {x, y}
            })) {
                reject({});
                return;
            }
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.SCROLL_BY,
                data: args,
                appDefinitionId: members._appDefId.get(this),
                callback});
        });
    }

    /**
     * @function getRouterData
     * @summary Returns the data that a router passed to the page in its response.
     * @returns {Object} The data returned by the router.
     * @description
     *  When you create a router and define its functionality in its [router()](wix-router.html#router)
     *  function, you can choose to send data along with the router's [response](wix-router.WixRouterResponse.html).
     *  That data is retrieved in the code of the page that was routed to using the
     *  `getRouterData()` function.
     *
     *  If you call the `getRouterData()` function from a non-router page or a router
     *  page that wasn't sent any data, the function returns `null`.
     * @memberof wix-window
     * @snippet [Window-getRouterData.es6=Get the data passed by a router]
     * @see [router( )](wix-router.html#router)
     * @see [WixRouterResponse](wix-router.WixRouterResponse.html)
     */
    getRouterData() {
        const appDefinitionId = members._appDefId.get(this);
        return self.routerData || platformUtils.getRGI().getRouterData(appDefinitionId) || null;
    }

    /**
     * @function getCurrentGeolocation
     * @summary Returns the current geolocation of the user.
     * @description
     *  The `getCurrentGeolocation()` function returns a Promise that resolves to an
     *  object containing the current geolocation of the user.
     *
     *  The object contains the following key:value pairs:
     *
     *  + `"timestamp"`: The geolocation timestamp representing the date and time at which the location was retrieved.
     *  + `"coords"`: An object that defines the location.
     *    + `"latitude"`: The position's latitude in decimal degrees.
     *    + `"longitude"`: The position's longitude in decimal degrees.
     *    + `"altitude"`: The position's altitude in metres, relative to sea level. This value may be null if the browser cannot provide the data.
     *    + `"accuracy"`: The accuracy in meters of the latitude and longitude properties.
     *    + `"altitudeAccuracy"`: The accuracy in meters of the altitude property. This value may be null.
     *    + `"heading"`: The direction in degrees in which the device is traveling. It indicates how far off from heading true north the device is. If `speed` is `0`, the heading is `NaN`. This value may be null if the browser cannot provide the data.
     *    + `"speed"`: The velocity in meters per second of the device. This value may be null if the browser cannot provide the data.
     *
     *
     * @note
     *  The `getCurrentGeolocation()` function has the following limitiations:
     *
     *  + On Chrome, the function only works on HTTPS sites.
     *  + On Chrome, Firefox, and Safari, the function only works if the user
     *    approves a popup.
     * @snippet [Window-getCurrentGeolocation.es6=Get the geolocation data]
     * @returns {Promise}
     * @fulfill {Object} The coordinates and timestamp of the current location.
     * @reject {String} The user blocked the geolocation popup.
     * @memberof wix-window
     */
    getCurrentGeolocation () {
        return new Promise((resolve, reject) => {
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.GET_CURRENT_GEOLOCATION,
                appDefinitionId: members._appDefId.get(this),
                callback
            });
        });
    }

    /**
     * @function openLightbox
     * @summary Opens a lightbox and optionally passes it the given data.
     * @description
     *  The `openLightbox()` function returns a Promise which is resolved when the
     *  lightbox closes. If the lightbox is closed programmatically using its
     *  [`close()`](wix-window.lightbox.html#close) function, and the
     *  [`close()`](wix-window.lightbox.html#close) function was invoked
     *  with a data parameter, then the Promise resolves to that data object.
     *
     *  If you send a data object to the lightbox, use the [`getContext()`](wix-window.lightbox.html#getContext)
     *  function in the lightbox's code to access the received data.
     *
     *  To pass data to the lightbox that is opened, you must open the lightbox
     *  programmatically using the [`openLightbox()`](wix-window.html#openLightbox)
     *  function. If the lightbox is opened automatically when the page loads or
     *  by a link from a page element, data will not be passed to the lightbox.
     *  Therefore, if you want to pass data to the lightbox, make sure
     *  **Automatically display lightbox on pages** is <a href="https://support.wix.com/en/article/setting-which-page-your-lightbox-appears-on" target="_blank">set</a> to **No** in the
     *  **Lightbox Settings** panel in the Editor and don't set any element's link
     *  to open the lightbox. Instead, create your own method for opening
     *  the lightbox programmatically. For example, you can add a button with an `onClick` event
     *  handler that calls the [`openLightbox()`](wix-window.html#openLightbox) function.
     *
     *  To pass data back to the page that opened the lightbox, you must close the
     *  lightbox programmatically using the `close()` function. If the lightbox is
     *  closed by the site visitor clicking the 'X' icon, close button, or lightbox
     *  overlay, data will not be passed back the the page that opened the lightbox.
     *  Therefore, if you want to make sure data is passed back to the page that
     *  opened the lightbox, <a href="https://support.wix.com/en/article/setting-how-your-lightbox-is-closed" target="_blank">disable
     *  all of the methods mentioned above</a> and create your own method for closing
     *  the lightbox programmatically. For example, you can add a button with an `onClick` event
     *  handler that calls the `close()` function.
     * @note
     *  Use the name of the lightbox and not the lightbox's ID when calling
     *  `openLightbox()`. You can find the lightbox's name by selecting the 
     *  lightbox and clicking the settings button.
     * @snippet [Window-openLightbox.es6=Open a lightbox]
     * @snippet [Window-openLightbox_options.es6=Open a lightbox and send it data]
     * @snippet [Window-openLightbox_then.es6=Open a lightbox and receive data when it is closed]
     * @snippet [Window-openLightbox_options_then.es6=Open a lightbox, send it data, and receive data back when it is closed]
     * @snippet [Lightbox-scenario.es6=A scenario where information is passed between a page and a lightbox]
     *  This example demonstrates how to pass data from a page to a lightbox that
     *  it opens and from the lightbox back to the page as it closes.
     *
     *  It assumes that the page has:
     *
     *  + An open button that is used to open the lightbox.
     *  + Two text inputs where information that is to be passed to the lightbox is entered.
     *  + Two text elements where information that is passed from the lightbox is displayed.
     *
     *
     *  It assumes that the lightbox has:
     *
     *  + An close button that is used to close the lightbox.
     *  + Two text inputs where information that is to be passed to the page is entered.
     *  + Two text elements where information that is passed from the page is displayed.
     * @param {String} name The name of the lightbox to open.
     * @param {Object} [data] The data to pass to the lightbox.
     * @returns {Promise}
     * @fulfill {Object} The returned data from the lightbox.
     * @reject {Error} The error that caused the rejection.
     * @memberof wix-window
     * @see [openModal( )](#openModal)
     */
    openLightbox (name, data) {
        return new Promise((resolve, reject) => {
            const isValid = validate({
                compType: 'Lightbox',
                functionName: 'openLightbox',
                dataToValidate: {name}
            });
            if (!isValid) {
                return reject('Lightbox title is not a valid input');
            }
            const popups = platformUtils.getRMI('wix-window.openLightbox').getSiteStructure().lightboxes;
            const popupInfo = _.find(popups, {name});


            if (!_.has(popupInfo, 'id')) {
                return reject(`There is no lightbox with the title "${name}".`);
            }
            const message = {id: popupInfo.id};
            if (!_.isUndefined(data)) {
                message.context = data;
            }
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.OPEN_LIGHTBOX,
                data: message,
                appDefinitionId: members._appDefId.get(this),
                callback
            });
        });
    }

    /**
     * @namespace wix-window.trackingParameters
     * @summary Objects used when calling the [`trackEvent()`](wix-window.html#trackEvent) function.
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~AddPaymentInfoEvent
     * @summary An object used when sending an AddPaymentInfo track event.
     * @property {String} [origin] - Origin of the event.
     * @property {String} [option] - Describes option information on a checkout page, such as selected payment method.
     * @snippet [Window-trackEvent-AddPaymentInfo.es6=Send an AddPaymentInfo trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~AddProductImpressionEvent
     * @summary An object used when sending an AddProductImpression track event.
     * @property {String} [origin] - Origin of the event.
     * @property {wix-window.trackingParameters~AddProductImpressionEventContents[]} contents Product information.
     * @snippet [Window-trackEvent-AddProductImpression.es6=Send an AddProductImpression trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~AddProductImpressionEventContents
     * @summary An object used when sending an AddProductImpression track event.
     * @property {String} [id] - Product ID.
     * @property {String} [sku] - Stock keeping unit code.
     * @property {String} name - Product name.
     * @property {String} [category] - Category of the page or product.
     * @property {Number} [price] - Product price.
     * @property {String} [currency] - Currency code.
     * @property {String} [brand] - Brand name of the product.
     * @property {String} [variant] - Variant of the product.
     * @property {String} [list] - List or collection the product is in.
     * @property {String} [position] - Product's position in a list or collection.
     * @snippet [Window-trackEvent-AddProductImpression.es6=Send an AddProductImpression trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~AddToCartEvent
     * @summary An object used when sending an AddToCart track event.
     * @property {String} [origin] - Origin of the event.
     * @property {String} [id] - Product ID.
     * @property {String} [sku] - Stock keeping unit code.
     * @property {String} name - Product name.
     * @property {Number} [price] - Product price.
     * @property {String} [currency] - Currency code.
     * @property {String} [category] - Category of the page or product.
     * @property {String} [brand] - Brand name of the product.
     * @property {String} [variant] - Variant of the product.
     * @property {String} [position] - Product's position in a list or collection.
     * @property {Number} [quantity] - Product quantity.
     * @snippet [Window-trackEvent-AddToCart.es6=Send an AddToCart trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~CheckoutStepEvent
     * @summary An object used when sending an CheckoutStep track event.
     * @property {String} [origin] - Origin of the event.
     * @property {String} [step] - Checkout step number. Be sure to report
     *  consistent step values. For example, use `3` when this step follows
     *  `StartPayment` and `AddPaymentInfo` events.
     * @property {String} [action] - Action the visitor has taken, such as `"Select shipping"`.
     * @property {String} [option] - Describes option information on a checkout page, such as selected payment method.
     * @snippet [Window-trackEvent-CheckoutStep.es6=Send an CheckoutStep trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~ClickProductEvent
     * @summary An object used when sending a ClickProduct track event.
     * @property {String} [origin] - Origin of the event.
     * @property {String} [id] - Product ID.
     * @property {String} [sku] - Stock keeping unit code.
     * @property {String} name - Product name.
     * @property {Number} [price] - Product price.
     * @property {String} [currency] - Currency code.
     * @property {String} [category] - Category of the page or product.
     * @property {String} [brand] - Brand name of the product.
     * @property {String} [variant] - Variant of the product.
     * @property {String} [list] - List or collection the product is in.
     * @property {String} [position] - Product's position in a list or collection.
     * @snippet [Window-trackEvent-ClickProduct.es6=Send a ClickProduct trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~CustomEvent
     * @summary An object used when sending a CustomEvent track event.
     * @description
     *  The object contains one required property, `event`, and any number of
     *  custom properties.
     *
     *  When sending a custom event to Google Analytics, include following parameters:
     *
     *  + `eventCategory`: string: object that was interacted with
     *  + `eventAction`: string: type of interaction
     *  + `eventLabel` (optional): string: event category
     *  + `eventValue` (optional): integer: numeric value associated with the event
     * @property {String} event - Product ID.
     * @snippet [Window-trackEvent-CustomEvent.es6=Send a CustomEvent trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~InitiateCheckoutEvent
     * @summary An object used when sending an InitiateCheckout track event.
     * @property {String} [origin] - Origin of the event.
     * @property {wix-window.trackingParameters~InitiateCheckoutEventContents[]} contents Product information.
     * @snippet [Window-trackEvent-InitiateCheckout.es6=Send an InitiateCheckout trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~InitiateCheckoutEventContents
     * @summary An object used when sending an InitiateCheckout track event.
     * @property {String} [id] - Product ID.
     * @property {String} [sku] - Stock keeping unit code.
     * @property {String} name - Product name.
     * @property {String} [category] - Category of the page or product.
     * @property {Number} [price] - Product price.
     * @property {String} [currency] - Currency code.
     * @property {String} [brand] - Brand name of the product.
     * @property {String} [variant] - Variant of the product.
     * @property {Number} [quantity] - Product quantity.
     * @snippet [Window-trackEvent-InitiateCheckout.es6=Send an InitiateCheckout trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~PurchaseEvent
     * @summary An object used when sending a Purchase track event.
     * @property {String} [origin] - Origin of the event.
     * @property {String} [id] - Product ID.
     * @property {String} [affiliation] - Name of the store where the event occurred.
     * @property {Number} [revenue] - Total amount of the purchase including shipping, taxes, etc.
     * @property {Number} [tax] - Total tax charge.
     * @property {Number} [shipping] - Total shipping charge.
     * @property {String} [coupon] - Coupon code applied to the transaction.
     * @property {wix-window.trackingParameters~PurchaseEventContents[]} contents Product information.
     * @snippet [Window-trackEvent-Purchase.es6=Send a Purchase trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~PurchaseEventContents
     * @summary An object used when sending an Purchase track event.
     * @property {String} [id] - Product ID.
     * @property {String} name - Product name.
     * @property {String} [category] - Category of the page or product.
     * @property {Number} [price] - Product price.
     * @property {String} [currency] - Currency code.
     * @property {String} [brand] - Brand name of the product.
     * @property {String} [variant] - Variant of the product.
     * @property {Number} [quantity] - Product quantity.
     * @snippet [Window-trackEvent-Purchase.es6=Send a Purchase trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~RemoveFromCartEvent
     * @summary An object used when sending a RemoveFromCart track event.
     * @property {String} [origin] - Origin of the event.
     * @property {String} [id] - Product ID.
     * @property {String} name - Product name.
     * @property {Number} [price] - Product price.
     * @property {String} [currency] - Currency code.
     * @property {String} [category] - Category of the page or product.
     * @property {String} [brand] - Brand name of the product.
     * @property {String} [variant] - Variant of the product.
     * @property {String} [position] - Product's position in a list or collection.
     * @property {Number} [quantity] - Product quantity.
     * @snippet [Window-trackEvent-RemoveFromCart.es6=Send a RemoveFromCart trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~StartPaymentEvent
     * @summary An object used when sending an StartPayment track event.
     * @property {String} [origin] - Origin of the event.
     * @property {String} [option] - Describes option information on a checkout page, such as selected payment method.
     * @snippet [Window-trackEvent-StartPayment.es6=Send a StartPayment trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @typedef {Object} wix-window.trackingParameters~ViewContentEvent
     * @summary An object used when sending a ViewContent track event.
     * @property {String} [origin] - Origin of the event.
     * @property {String} [id] - Product ID.
     * @property {String} [sku] - Stock keeping unit code.
     * @property {String} name - Product name.
     * @property {Number} [price] - Product price.
     * @property {String} [currency] - Currency code.
     * @property {String} [category] - Category of the page or product.
     * @property {String} [brand] - Brand name of the product.
     * @property {String} [variant] - Variant of the product.
     * @property {String} [list] - List or collection the product is in.
     * @property {String} [position] - Product's position in a list or collection.
     * @snippet [Window-trackEvent-ViewContent.es6=Send a ViewContent trackEvent]
     * @see [trackEvent( )](wix-window.html#trackEvent)
     */

    /**
     * @function trackEvent
     * @summary Sends a tracking event to external analytics tools.
     * @description
     *  The `trackEvent()` function sends an event to analytics tools connected
     *  to your site. It can send events to Google Analytics and Facebook Pixel.
     *  To learn how to connect analytics tools to your site, see
     *  [About Tracking Tools & Analytics](https://support.wix.com/en/article/about-tracking-tools-analytics).
     *
     *  The following events and parameters are available:
     *
     *  + `"AddProductImpression"`: [parameters](wix-window.trackingParameters.html#AddProductImpressionEvent): When a user views a product. (Google Analytics only)
     *  + `"ClickProduct"`: [parameters](wix-window.trackingParameters.html#ClickProductEvent): When a user clicks on a product. (Google Analytics only)
     *  + `"ViewContent"`: [parameters](wix-window.trackingParameters.html#ViewContentEvent): When a key page is viewed.
     *  + `"AddToCart"`: [parameters](wix-window.trackingParameters.html#AddToCartEvent): When a user adds a product to the shopping cart.
     *  + `"RemoveFromCart"`: [parameters](wix-window.trackingParameters.html#RemoveFromCartEvent): When a user removes a product from the shopping cart. (Google Analytics only)
     *  + `"InitiateCheckout"`: [parameters](wix-window.trackingParameters.html#InitiateCheckoutEvent): When a user starts the checkout process.
     *  + `"StartPayment"`: [parameters](wix-window.trackingParameters.html#StartPaymentEvent): When a user starts the payment process. (Google Analytics only)
     *  + `"AddPaymentInfo"`: [parameters](wix-window.trackingParameters.html#AddPaymentInfoEvent): When a user saves payment information.
     *  + `"CheckoutStep"`: [parameters](wix-window.trackingParameters.html#CheckoutStepEvent): When a user completes a custom checkout step. (Google Analytics only)
     *  + `"Purchase"`: [parameters](wix-window.trackingParameters.html#PurchaseEvent): When the checkout process is complete.
     *  + `"Lead"`: When a user subscribes to a newsletter or submits a contact form.
     *  + `"CustomEvent"`: [parameters](wix-window.trackingParameters.html#CustomEvent): When a user performs an event not listed above.
     *
     *
     * @note
     *  The `trackEvent()` function only runs on published versions of your site.
     *  It does not work when previewing your site.
     * @snippet [Window-trackEvent-ViewContent.es6=Send a trackEvent with parameters]
     * @snippet [Window-trackEvent-Lead.es6=Send a trackEvent that doesn't take any parameters]
     * @snippet [Window-trackEvent-onReady.es6=Send a trackEvent from the onReady event handler]
     * @param {String} eventName The name of the event.
     * @param {wix-window.trackingParameters~AddPaymentInfoEvent | wix-window.trackingParameters~AddProductImpressionEvent | wix-window.trackingParameters~AddToCartEvent | wix-window.trackingParameters~ClickProductEvent | wix-window.trackingParameters~CustomEvent | wix-window.trackingParameters~InitiateCheckoutEvent | wix-window.trackingParameters~PurchaseEvent | wix-window.trackingParameters~RemoveFromCartEvent | wix-window.trackingParameters~ViewContentEvent} parameters The event's parameters.
     * @memberof wix-window
     */
    trackEvent(eventName, data, options) {
        if (this.rendering.env === 'backend') {
            return;
        }

        const validtionObj = {data, options};
        if (!_.isNil(eventName)) {
            validtionObj.eventName = eventName;
        }
        if (!validate({
            compType: 'trackEvent',
            functionName: 'trackEvent',
            dataToValidate: validtionObj,
            shouldCheckMandatoryFields: true
        })) {
            return;
        }

        const message = {eventName};
        if (!_.isNil(data)) {
            message.params = data;
        }
        if (!_.isNil(options)) {
            message.options = options;
        }
        postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
            command: postMessage.MessageTypes.TRACK_EVENT,
            data: message,
            appDefinitionId: members._appDefId.get(this)
        });
    }

    /**
     * @typedef {Object} wix-window~OpenModalOptions
     * @summary An object used when opening a modal window.
     * @property {Number} width - Width of the modal window.
     * @property {Number} height - Height of the modal window.
     * @snippet [Window-openModal.es6=Open a modal window]
     * @see [openModal( )](#openModal)
     */

    /**
     * @function openModal
     * @summary Opens a modal window that displays the specified web page.
     * @description
     *  A modal window displays the page specified by the `url` property over
     *  your current page. Unlike a [lightbox](wix-window.lightbox.html), which
     *  is opened using the [`openLightbox()`](#openLightbox) function, the window
     *  opened by `openModal()` is not part of your site structure.
     *
     *  Only one modal window can be open at any given time. Therefore, opening
     *  a modal window closes an already open modal window if there is one.
     * @note
     *  The specified `url` must be an HTTPS URL. To use an HTTP URL, [turn off
     *  SSL for your site](https://support.wix.com/en/article/turning-ssl-on-or-off-for-your-site).
     * @snippet [Window-openModal.es6=Open a modal window]
     * @snippet [Window-openModal_then.es6=Open a modal window and log a message when it is closed]
     * @param {String} url The URL of the page to show in the modal window.
     * @param {wix-window~OpenModalOptions} options The options used for the modal window.
     * @returns {Promise}
     * @fulfill {void} When the modal window is closed.
     * @reject {Error} The error that caused the rejection.
     * @memberof wix-window
     * @see [openLightbox( )](#openLightbox)
     */
    openModal(url, options, compId) {
        return new Promise((resolve, reject) => {
            const isValid = validate({
                compType: 'modal',
                functionName: 'openModal',
                dataToValidate: {url, options},
                shouldCheckMandatoryFields: true
            });
            if (!isValid) {
                return reject('Modal arguments are not defined correctly');
            }

            if (!urlUtils.isValidProtocol(url, platformUtils.getRGI())) {
                return reject(`Mix protocol is not allowed. Modal url protocol is http, and the page is https`);
            }

            const {width, height, theme} = options;
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.OPEN_MODAL,
                data: {
                    url,
                    width,
                    height,
                    theme,
                    compId
                },
                appDefinitionId: members._appDefId.get(this),
                callback
            });
        });
    }

    /**
     * @function postMessage
     * @summary Sends a message to the page's parent.
     * @description
     *  If a page is embedded within another site, using an [HtmlComponent]($w.HtmlComponent.html)
     *  on a Wix site or an [iframe](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe)
     *  on a non-Wix site, you can use the `postMessage()` function to send a
     *  message from the inner site to the outer site.
     *
     *  When the parent site is a Wix site, use the [`onMessage()`]($w.HtmlComponent.html#onMessage)
     *  function to receive the message on the parent page.
     *
     *  When the parent site is a non-Wix site, use the page's `window.onmessage`
     *  event handler to read the `data` property of the received `MessageEvent`
     *  to receive the message on the parent page.
     * @snippet [Window-postMessage_wix.es6=Send a message to a Wix parent site]
     * @snippet [Window-postMessage_nonwix.es6=Send a message to a non-Wix parent site]
     * @param {Object} message The message to send.
     * @param {String} [target] The target to send the message to. Must be `"parent"` or omitted. Defaults to `"parent"`.
     * @returns {Promise}
     * @fulfill {Object} The data returned from the modal window.
     * @reject {Error} The error that caused the rejection.
     * @memberof wix-window
     */
    postMessage(payload, target){
        const appDefinitionId = members._appDefId.get(this);
        if (!validate({
            compType: 'Window',
            functionName: 'postMessage',
            dataToValidate: {payload, target}
        })) {
            return;
        }
        postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
            command: postMessage.MessageTypes.SEND_MESSAGE_TO_PARENT,
            data: payload,
            appDefinitionId
        });
    }


    getContainerPort(appDefId){
        return new Promise((resolve, reject) => {
            const cb = (data, error, ports) => {
                if (error){
                    reject(error);
                    return;
                }
                if (ports){
                    this.portData.port = ports[0];
                    resolve(ports[0]);
                }
            };
            const appDefinitionId = appDefId || members._appDefId.get(this);
            if (!validate({
                    compType: 'Window',
                    functionName: 'getContainerPort',
                    dataToValidate: {appDefinitionId}
                })) {
                return;
            }
            if (this.portData.port){
                resolve(this.portData.port);
                return;
            }
            if (this.portData.requestInPorcess) {
                this.portData.callbacks.push(cb);
                return;
            }
            this.portData.requestInPorcess = true;
            const callback = (data, error, ports) => {
                this.portData.requestInPorcess = false;
                cb(data, error, ports);
                _.forEach(this.portData.callbacks, (callback) => {
                    callback(data, error, ports);
                });
                this.portData.callbacks = [];
            };
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.OPEN_MESSAGE_CHANNEL,
                data: {appDefinitionId},
                appDefinitionId,
                callback
            });
        });

    }

   /**
    * @namespace wix-window.multilingual
    * @summary The Multilingual API is used when working with the languages in a multilingual site.
    * @description
    *  A multilingual site is a site that is set up to display in more than one
    *  language. To learn more about enabling multilingual functionality in your
    *  site, see <a href="https://support.wix.com/en/article/about-wix-multilingual" target="_blank">About Wix Multilingual</a>.
    *
    *  To use the Multilingual API, import `wixWindow` from the `wix-window` module:
    *
    *    ```javascript
    *    import wixWindow from 'wix-window';
    *    ```
    *
    * The APIs in `wix-window.multilingual` can only be used in front-end code.
    */
    get multilingual() {
        return this._multilingual;
    }

  /**
   * @member isEnabled
   * @label property
   * @summary Gets whether the site has been set up to be shown in multiple languages.
   * @snippet [Multilingual-isEnabled.es6=Get whether the site is set up to be shown in multiple languages]
   * @type {Boolean}
   * @memberof wix-window.multilingual
   **/

  /**
   * @member currentLanguage
   * @label property
   * @summary Sets or gets the site's current display language.
   * @description
   *  Setting the `currentLanguage` property changes the site's display language.
   *  The current page is reloaded in the newly set language.
   *
   *  Set the current language using a two-letter language code. The code must
   *  represent one of <a href="https://support.wix.com/en/article/managing-languages-in-a-multilingual-site" target="_blank">the languages set to show on your site</a>.
   *  You can retrieve your site's languages and corresponding language codes using
   *  the [`siteLanguages`](#siteLanguages) property.
   *
   *  Getting the `currentLanguage` property gets the two-letter language code
   *  of the site's current display language.
   * @snippet [Multilingual-currentLanguage_get.es6=Get the site's current display language]
   * @snippet [Multilingual-currentLanguage_set.es6=Set the site's current display language]
   * @snippet [Multilingual-button_toggle.es6=Create a button used to toggle between two languages]
   *  This example assumes you have a site set up to display in both English and
   *  Spanish. It also assumes you have a button with the ID **toggleLanguage**.
   *
   *  An `onClick` event handler is registered for the button. The event handler
   *  checks the site's current language and then sets the site to change to the
   *  other available language.
   * @snippet [Multilingual-dropdown.es6=Create a dropdown used to change between available languages]
   *  This example assumes you have a site set up to display in multiple languages.
   *  It also assumes you have a dropdown element with the ID **languageDropdown**.
   *
   *  When the page loads, the site's available languages are retrieved. Then,
   *  the JavaScript `map()` function is used to change the retrieved language
   *  information into the format expected by the dropdown's `options` property.
   *  Next, the JavaScript `filter()` function is used remove the current language
   *  from the list. Finally, the dropdown's `options` property is set using the
   *  formatted language list.
   *
   *  An `onChange` event handler is registered for the dropdown element. The event
   *  handler changes the site's current language based on the language chosen by
   *  the user.
   * @snippet [Multilingual-filter_dataset.es6=Filter a dataset based on the site's current display language]
   *  This example assumes you have a site set up to display in both English and
   *  Spanish. It also assumes you have a collection that contains data in both
   *  languages. The collection has a **language** field that indicates each item's
   *  language. The following code filters a dataset connected to the collection
   *  so it only contains items that correspond to the site's current language.
   * @type {String}
   * @memberof wix-window.multilingual
   **/

  /**
   * @typedef {Object} wix-window.multilingual~SiteLanguage
   * @summary An object returned by the `siteLanguages` property that contains information about the site's languages.
   * @property {String} name The language's full name.
   * @property {String} locale The language's locale code, which represents a set of language-related formatting preferences.
   * @property {String} languageCode The language's two-letter code.
   * @property {String} countryCode The language's three-letter country code.
   * @property {Boolean} isPrimaryLanguage Whether the language is the site's primary language.
   * @snippet [Multilingual-siteLanguages_get.es6=Get information about the site's languages]
   * @snippet [Multilingual-dropdown.es6=Create a dropdown used to change between available languages]
   *  This example assumes you have a site set up to display in multiple languages.
   *  It also assumes you have a dropdown element with the ID **languageDropdown**.
   *
   *  When the page loads, the site's available languages are retrieved. Then,
   *  the JavaScript `map()` function is used to change the retrieved language
   *  information into the format expected by the dropdown's `options` property.
   *  Next, the JavaScript `filter()` function is used remove the current language
   *  from the list. Finally, the dropdown's `options` property is set using the
   *  formatted language list.
   *
   *  An `onChange` event handler is registered for the dropdown element. The event
   *  handler changes the site's current language based on the language chosen by
   *  the user.
   * @see [siteLanguages](#siteLanguages)
   */

  /**
   * @member siteLanguages
   * @summary Gets information about the site's languages.
   * @description
   * The `siteLanguages` property returns an array of [`SiteLanguage`](#SiteLanguage)
   * objects containing information about all the languages that the site is set
   * to display in.
   * @snippet [Multilingual-siteLanguages_get.es6=Get information about the site's languages]
   * @snippet [Multilingual-dropdown.es6=Create a dropdown used to change between available languages]
   *  This example assumes you have a site set up to display in multiple languages.
   *  It also assumes you have a dropdown element with the ID **languageDropdown**.
   *
   *  When the page loads, the site's available languages are retrieved. Then,
   *  the JavaScript `map()` function is used to change the retrieved language
   *  information into the format expected by the dropdown's `options` property.
   *  Next, the JavaScript `filter()` function is used remove the current language
   *  from the list. Finally, the dropdown's `options` property is set using the
   *  formatted language list.
   *
   *  An `onChange` event handler is registered for the dropdown element. The event
   *  handler changes the site's current language based on the language chosen by
   *  the user.
   * @type {wix-window.multilingual~SiteLanguage[]}
   * @memberof wix-window.multilingual
   */
}

setStageUnfriendlyFunctions(Window, ['openLightbox', 'openModal', 'scrollTo', 'scrollBy']);
export default Window;
